import {
  getV1CompaniesCompanyIdAccountExternalTruckIdentifiers,
  patchV1CompaniesAccountExternalTruckIdentifiersId,
} from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { useState } from 'react';

import { MAX_API_PAGE_SIZE } from '~constants/consts';
import connection from '~services/connectionModule';
import { extractPagination, PaginationLink } from '~services/pagination';

import { AccountExternalTruckIdentifier } from './models';

interface GetAccountExternalTruckIdentifiersByCompanyIdProps {
  accountId: string;
  companyId: string;
  cursor?: string;
  link?: PaginationLink;
  signal?: AbortSignal;
}

interface UpdateAccountExternalTruckIdentifierProps {
  companyId: string;
  truckIdentifierId: string;
  companyShareId: string | null;
}

export const useAccountExternalTruckIdentifier = () => {
  const [isUpdating, setIsUpdating] = useState(false);
  const getAccountExternalTruckIdentifiersByCompanyId = async ({
    accountId,
    companyId,
    cursor,
    link,
    signal,
  }: GetAccountExternalTruckIdentifiersByCompanyIdProps) => {
    try {
      const response = await getV1CompaniesCompanyIdAccountExternalTruckIdentifiers({
        path: { 'company-id': companyId },
        query: {
          'filter[account_id]': accountId,
          'page[limit]': MAX_API_PAGE_SIZE,
          ...(link && cursor ? { [`page[${link}]`]: cursor } : {}),
        },
        signal,
      });

      return {
        data: response.data.data.map(AccountExternalTruckIdentifier.parse),
        pagination: extractPagination(response),
      };
    } catch (e) {
      if (signal?.aborted) {
        throw e;
      }

      connection.handleRequestError(
        e,
        t('error_messages.account_external_truck_identifiers.failed_to_fetch'),
      );
      throw e;
    }
  };

  const updateAccountExternalTruckIdentifier = async ({
    companyId,
    truckIdentifierId,
    companyShareId,
  }: UpdateAccountExternalTruckIdentifierProps) => {
    try {
      setIsUpdating(true);
      const response = await patchV1CompaniesAccountExternalTruckIdentifiersId({
        path: {
          'company-id': companyId,
          id: truckIdentifierId,
        },
        body: {
          company_share_id: companyShareId,
        },
      });
      return AccountExternalTruckIdentifier.parse(response.data.data);
    } catch (e) {
      connection.handleRequestError(
        e,
        t('error_messages.account_external_truck_identifiers.failed_to_update'),
      );
      throw e;
    } finally {
      setIsUpdating(false);
    }
  };

  return {
    isUpdating,
    getAccountExternalTruckIdentifiersByCompanyId,
    updateAccountExternalTruckIdentifier,
  };
};
